import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, orderBy, limit, startAfter } from "firebase/firestore";
import { db } from '../../firebase';
import { useNavigate, Link, Navigate } from 'react-router-dom';
import { ChevronDownIcon, ChevronUpIcon, PencilSquareIcon, MagnifyingGlassIcon, ArrowDownTrayIcon, FolderOpenIcon, MagnifyingGlassCircleIcon } from '@heroicons/react/24/outline';
import MoonLoader from 'react-spinners/MoonLoader';
import BreadCrumb from '../BreadCrumb';
import { useAuth } from '../Auth/Auth';
import { formatDate } from '../Help/helper';
import DuplicateManager from '../Utils/DuplicateManager';
import logger from '../../utils/logger';
import { CSVLink } from 'react-csv';



const pages = [
  { name: 'Service Advisor', to: '/vehicles', component: Link, current: false },
  { name: 'All Job Cards', to: '/vehicles/alljobs', component: Link, current: true },  
];

const AllJobs = () => {
  const [jobs, setJobs] = useState([]);
  const { role } = useAuth();
  const [sortConfig, setSortConfig] = useState({ key: 'jobCardNo', direction: 'ascending' });
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [jobsPerPage] = useState(10);
  const navigate = useNavigate();
  const [searchCategory, setSearchCategory] = useState('customerName');
  const [searchQuery, setSearchQuery] = useState('');
  const [showDuplicateManager, setShowDuplicateManager] = useState(false);
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    fetchJobs();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchJobs = async (loadMore = false) => {
    setIsLoading(true);

    try {
      const jobsCollection = collection(db, 'jobs');

      // Pagination: Fetch only 50 jobs at a time, ordered by dateCreated
      const PAGE_SIZE = 50;
      let jobsQuery = query(
        jobsCollection,
        orderBy('createdAt', 'desc'),
        limit(PAGE_SIZE)
      );

      // If loading more, start after the last document
      if (loadMore && lastVisible) {
        jobsQuery = query(
          jobsCollection,
          orderBy('createdAt', 'desc'),
          startAfter(lastVisible),
          limit(PAGE_SIZE)
        );
      }

      const jobsSnapshot = await getDocs(jobsQuery);

      if (jobsSnapshot.docs.length > 0) {
        // Store the last visible document for pagination
        setLastVisible(jobsSnapshot.docs[jobsSnapshot.docs.length - 1]);
        setHasMore(jobsSnapshot.docs.length === PAGE_SIZE);
      } else {
        setHasMore(false);
      }

      const jobsList = jobsSnapshot.docs.map((docSnapshot) => {
        const jobData = docSnapshot.data();
        return {
          ...jobData,
          id: docSnapshot.id,
        };
      });

      // If loading more, append to existing jobs; otherwise replace
      if (loadMore) {
        setJobs(prevJobs => [...prevJobs, ...jobsList]);
      } else {
        setJobs(jobsList);
        setLastVisible(null);
      }
    } catch (error) {
      logger.error("Error fetching jobs:", error);
    }

    setIsLoading(false);
  };
  

  const handleSearch = async () => {
    if (!searchQuery) {
      fetchJobs();
      return;
    }

    setIsLoading(true);

    const jobsCollection = collection(db, 'jobs');
    let searchQueryRef;

    if (searchCategory === 'customerName') {
      searchQueryRef = query(jobsCollection, where('customerName', '>=', searchQuery), where('customerName', '<=', searchQuery + '\uf8ff'));
    } else if (searchCategory === 'VIN') {
      searchQueryRef = query(jobsCollection, where('VIN', '>=', searchQuery), where('VIN', '<=', searchQuery + '\uf8ff'));
    } else if (searchCategory === 'jobCardNo') {
      searchQueryRef = query(jobsCollection, where('jobCardNo', '>=', searchQuery), where('jobCardNo', '<=', searchQuery + '\uf8ff'));
    } else if (searchCategory === 'registrationNumber') {
      searchQueryRef = query(jobsCollection, where('registrationNumber', '>=', searchQuery), where('registrationNumber', '<=', searchQuery + '\uf8ff'));
    }

    const jobsSnapshot = await getDocs(searchQueryRef);
    const jobsList = jobsSnapshot.docs.map((docSnapshot) => ({
      id: docSnapshot.id,
      ...docSnapshot.data(),
    }));

    setJobs(jobsList);
    setIsLoading(false);
  };

  const sortData = (key, direction) => {
    setSortConfig({
      key: key,
      direction: direction
    });
  };

  const sortedJobs = () => {
    if (!sortConfig) return jobs;
    return [...jobs].sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === 'ascending' ? -1 : 1;
      if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === 'ascending' ? 1 : -1;
      return 0;
    });
  };

  const currentJobs = sortedJobs().slice((currentPage - 1) * jobsPerPage, currentPage * jobsPerPage);
  const totalPages = Math.ceil(jobs.length / jobsPerPage);
  const changePage = (pageNumber) => setCurrentPage(pageNumber);

  const navigateToJobDetail = (jobCardNo) => {
    navigate(`/vehicles/jobupdate/${jobCardNo}`);
  };

  if (role !== 'Service Advisor' && role !== 'Admin') {
    return <Navigate to="/unauthorized" replace />;
  }
 



  // CSV export setup
  const csvHeaders = [
    { label: 'Job No', key: 'jobCardNo' },
    { label: 'Customer Name', key: 'customerName' },
    { label: 'Reg No', key: 'registrationNumber' },
    { label: 'VIN', key: 'VIN' },
    { label: 'Vehicle Type', key: 'vehicleMake' },
    { label: 'Date Created', key: 'createdAt' },
    { label: 'Status', key: 'status' },
  ];

  const normalizeJob = (j) => ({
    jobCardNo: j?.jobCardNo ?? '',
    customerName: j?.customerName ?? '',
    registrationNumber: j?.registrationNumber ?? '',
    VIN: j?.VIN ?? '',
    vehicleMake: `${j?.vehicleMake ?? ''} ${j?.vehicleModel ?? ''}`.trim(),
    createdAt: j?.createdAt?.toDate ? formatDate(j.createdAt.toDate()) : (j?.createdAt ? formatDate(j.createdAt) : ''),
    status: j?.status ?? '',
  });

  const csvDataAll = jobs.map(normalizeJob);
  const csvDataPage = currentJobs.map(normalizeJob);

  return (
    <>
      <BreadCrumb pages={pages} />

      {/* Gradient Background */}
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 py-8">
        <div className="container mx-auto px-4">

          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl shadow-lg">
                <FolderOpenIcon className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-4xl font-black text-gray-900">All Job Cards</h1>
                <p className="text-gray-600 mt-1">Complete overview of all service job cards</p>
              </div>
            </div>

            {/* Stats Card */}
            <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl shadow-lg p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-indigo-100 text-sm font-semibold uppercase tracking-wide">Total Jobs</p>
                  <p className="text-4xl font-black mt-2">{jobs.length}</p>
                </div>
                <FolderOpenIcon className="h-16 w-16 text-indigo-200 opacity-50" />
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className="flex justify-center items-center h-64 bg-white rounded-2xl shadow-lg">
              <div className="text-center">
                <MoonLoader size={60} color="#6366F1" />
                <p className="mt-4 text-gray-600 font-medium">Loading all jobs...</p>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">

              {/* Search and Action Controls */}
              <div className="p-6 bg-gradient-to-r from-gray-50 to-indigo-50 border-b border-gray-200">
                <div className="flex flex-col gap-4">

                  {/* Search Section */}
                  <div className="flex flex-col lg:flex-row gap-3">
                    <select
                      value={searchCategory}
                      onChange={(e) => setSearchCategory(e.target.value)}
                      className="px-4 py-2.5 border border-gray-300 rounded-xl text-sm font-medium focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                    >
                      <option value="customerName">Customer Name</option>
                      <option value="VIN">VIN</option>
                      <option value="jobCardNo">Job Card No</option>
                      <option value="registrationNumber">Registration No</option>
                    </select>

                    <div className="flex-1 relative">
                      <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={`Search by ${searchCategory}...`}
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                      />
                    </div>

                    <button
                      onClick={handleSearch}
                      className="px-6 py-2.5 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl font-semibold hover:from-indigo-600 hover:to-purple-700 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 flex items-center justify-center gap-2"
                    >
                      <MagnifyingGlassIcon className="h-5 w-5" />
                      Search
                    </button>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setShowDuplicateManager(true)}
                      className="px-4 py-2.5 bg-gradient-to-r from-red-500 to-pink-600 text-white rounded-xl font-semibold hover:from-red-600 hover:to-pink-700 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 flex items-center gap-2 text-sm"
                    >
                      <MagnifyingGlassCircleIcon className="h-5 w-5" />
                      Find Duplicates
                    </button>

                    <CSVLink
                      data={csvDataPage}
                      headers={csvHeaders}
                      filename={`all_jobs_page_${currentPage}.csv`}
                      className="px-4 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl font-semibold hover:from-emerald-600 hover:to-teal-700 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 flex items-center gap-2 text-sm"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4" />
                      Page CSV
                    </CSVLink>

                    <CSVLink
                      data={csvDataAll}
                      headers={csvHeaders}
                      filename={`all_jobs_complete.csv`}
                      className="px-4 py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-semibold hover:from-green-600 hover:to-emerald-700 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 flex items-center gap-2 text-sm"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4" />
                      All CSV
                    </CSVLink>
                  </div>
                </div>
              </div>

              {/* Table */}
              {currentJobs.length === 0 ? (
                <div className="p-12 text-center">
                  <FolderOpenIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 mb-2">No Jobs Found</h3>
                  <p className="text-gray-600">There are currently no job cards to display.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gradient-to-r from-indigo-500 to-purple-600">
                      <tr>
                        {[
                          { label: 'Job No', key: 'jobCardNo' },
                          { label: 'Customer Name', key: 'customerName' },
                          { label: 'Reg No', key: 'registrationNumber' },
                          { label: 'VIN', key: 'VIN' },
                          { label: 'Vehicle Type', key: 'vehicleMake' },
                          { label: 'Date Created', key: 'createdAt' },
                        ].map((header) => (
                          <th
                            key={header.key}
                            className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider cursor-pointer hover:bg-indigo-600 transition-colors duration-150"
                            onClick={() => {
                              const newDirection = sortConfig.key === header.key && sortConfig.direction === 'ascending' ? 'descending' : 'ascending';
                              sortData(header.key, newDirection);
                            }}
                          >
                            <div className="flex items-center gap-2">
                              {header.label}
                              {sortConfig.key === header.key && (
                                sortConfig.direction === 'ascending'
                                  ? <ChevronUpIcon className="h-4 w-4" />
                                  : <ChevronDownIcon className="h-4 w-4" />
                              )}
                            </div>
                          </th>
                        ))}
                        <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {currentJobs.map((job, index) => (
                        <tr
                          key={job.id}
                          className="hover:bg-indigo-50 transition-colors duration-150 cursor-pointer"
                          onClick={() => navigateToJobDetail(job.jobCardNo)}
                        >
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="text-sm font-bold text-indigo-600">{job.jobCardNo}</span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="text-sm font-medium text-gray-900">{job.customerName}</span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="text-sm text-gray-600">{job.registrationNumber}</span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="text-sm text-gray-600">{job?.VIN}</span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="text-sm text-gray-600">{`${job.vehicleMake} ${job.vehicleModel}`}</span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="text-sm text-gray-600">{formatDate(job.createdAt.toDate())}</span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                              job.status === 'Open'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-gray-100 text-gray-700'
                            }`}>
                              {job.status || 'Unknown'}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigateToJobDetail(job.jobCardNo);
                              }}
                              className="p-2 text-indigo-600 hover:bg-indigo-100 rounded-lg transition-all duration-200 hover:shadow-md"
                            >
                              <PencilSquareIcon className="h-5 w-5" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {currentJobs.length > 0 && totalPages > 0 && (
                <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-indigo-50 border-t border-gray-200">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-600">
                        Showing {(currentPage - 1) * jobsPerPage + 1}–{Math.min(currentPage * jobsPerPage, jobs.length)} of {jobs.length}
                      </span>
                    </div>

                    {/* Pagination controls */}
                    <div className="flex items-center gap-1 flex-wrap">
                      {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => i + 1).map((n) => (
                        <button
                          key={n}
                          onClick={() => changePage(n)}
                          className={`px-3 py-1.5 text-sm font-medium border rounded-lg transition-all duration-200 ${
                            n === currentPage
                              ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white border-transparent shadow-md'
                              : 'bg-white text-gray-700 border-gray-300 hover:bg-indigo-50'
                          }`}
                        >
                          {n}
                        </button>
                      ))}
                      {totalPages > 10 && (
                        <span className="text-sm text-gray-500 px-2">...</span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Load More Button */}
              {hasMore && !isLoading && jobs.length > 0 && (
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-center">
                  <button
                    onClick={() => fetchJobs(true)}
                    className="px-6 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg hover:from-indigo-600 hover:to-purple-700 transition-all duration-200 shadow-md hover:shadow-lg"
                  >
                    Load More Jobs
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Duplicate Manager Modal */}
      <DuplicateManager
        isOpen={showDuplicateManager}
        onClose={() => setShowDuplicateManager(false)}
        items={jobs}
        collectionName="jobs"
        duplicateCriteria={['jobCardNo', 'customerName', 'registrationNumber']}
        itemLabel="Job"
      />
    </>
  );
};


export default AllJobs;
